<script lang="ts">
import { computed, defineComponent, inject, watch } from "vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import InfoBox from "@/components/grid/InfoBox.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "Zahlungsdaten.vue",
  components: {
    InfoBox,
    Zelle,
    TitelZelle,
    Zeile,
    Ueberschrift,
    ValidierungsFehler,
  },
  props: {
    fehler: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const piniaStore = useFormStore();
    const { formatCurrency, formatIban } = formatUtils();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const { istFhkVerkaufsprodukt } = storeUtils();
    const istEinmalbeitrag = computed(() => {
      return (
        piniaStore.state.verkaufsprodukt.toLowerCase() === "bl" ||
        piniaStore.state.verkaufsprodukt.toLowerCase() === "bh"
      );
    });
    const istFerienhaus = computed(() => {
      return istFhkVerkaufsprodukt(piniaStore.state);
    });
    const gesamtPreisGemZahlweiseFH = computed(() => {
      return piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].gemZahlungsweise;
    });
    const gesamtPreisJaehrlichFH = computed(() => {
      return piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].jaehrlich;
    });
    const gesamtPreisHalbjaehrlichFH = computed(() => {
      return piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].halbjaehrlich;
    });
    const gesamtPreisVieteljaehrlichFH = computed(() => {
      return piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].vierteljaehrlich;
    });
    const gesamtPreisMonatlichFH = computed(() => {
      return piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].monatlich;
    });
    const pruefeHalbjaehrlichFH = computed(() => {
      for (let i = 0; i <= piniaStore.state.preise.produktLinien.length - 1; i++) {
        if (piniaStore.state.preise.produktLinien[i].halbjaehrlich < 25.0) {
          return false;
        }
      }
      return true;
    });
    const pruefeVierteljaehrlichFH = computed(() => {
      for (let i = 0; i <= piniaStore.state.preise.produktLinien.length - 1; i++) {
        if (piniaStore.state.preise.produktLinien[i].vierteljaehrlich < 25.0) {
          return false;
        }
      }
      return true;
    });
    const pruefeMonatlichFH = computed(() => {
      for (let i = 0; i <= piniaStore.state.preise.produktLinien.length - 1; i++) {
        if (piniaStore.state.preise.produktLinien[i].monatlich < 25.0) {
          return false;
        }
      }
      return true;
    });

    watch(
      () => piniaStore.state.zahlungsdaten.iban,
      (neu) => {
        piniaStore.state.zahlungsdaten.iban = formatIban(neu);
      },
    );

    watch(
      () => piniaStore.state.zahlungsdaten.zahlungsart,
      () => {
        if (
          piniaStore.state.zahlungsdaten.zahlungsart == "invoice" &&
          piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle == "anderePerson"
        ) {
          piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle = "versicherungsnehmer";
        }
      },
    );

    const rechnungszahlungAktiv = computed(() => {
      return Number(piniaStore.state.tarifinformationen.zahlungsweise) === 1 || istEinmalbeitrag.value;
    });
    watch(
      () => piniaStore.state.tarifinformationen.zahlungsweise,
      (zahlungsweise) => {
        if (Number(zahlungsweise) !== 1 && !istEinmalbeitrag.value) {
          piniaStore.state.zahlungsdaten.zahlungsart = "debit";
        }
      },
    );

    const kontoinhaberIstAnderePerson = computed(() => {
      return (
        piniaStore.state.zahlungsdaten.zahlungsart == "debit" &&
        piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle == "anderePerson"
      );
    });

    watch(
      () => piniaStore.state.personendaten.verwalterAktiv,
      () => {
        if (
          !piniaStore.state.personendaten.verwalterAktiv &&
          piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle === "verwalter"
        ) {
          piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle = "";
        }
      },
    );

    return {
      piniaStore,
      formatCurrency,
      istSichtbar,
      wechsleSichtbarkeit,
      istEinmalbeitrag,
      istFerienhaus,
      rechnungszahlungAktiv,
      kontoinhaberIstAnderePerson,
      gesamtPreisJaehrlichFH,
      pruefeHalbjaehrlichFH,
      gesamtPreisHalbjaehrlichFH,
      pruefeVierteljaehrlichFH,
      gesamtPreisVieteljaehrlichFH,
      gesamtPreisMonatlichFH,
      pruefeMonatlichFH,
      gesamtPreisGemZahlweiseFH,
    };
  },
});
</script>

<template>
  <ueberschrift titel="Zahlungsdaten"></ueberschrift>
  <zeile v-if="!istEinmalbeitrag && !istFerienhaus">
    <titel-zelle :zeige-info="true" info-box-id="tariffPaymentFrequencyInfo">Zahlungsweise</titel-zelle>
    <zelle>
      <select
        id="tariffPaymentFrequency"
        name="tariffPaymentFrequency"
        v-model.number="piniaStore.state.tarifinformationen.zahlungsweise"
        class="c-form-control-select"
        data-cy="zahlungsweise"
        required
        aria-required="true"
        tabindex="0"
        data-fehlermeldung="Bitte wählen Sie eine Zahlungsweise aus."
        aria-label="Zahlungsweise"
        aria-describedby="tariffPaymentFrequencyInfo"
      >
        <option v-bind:value="1">
          Jährlich -
          {{
            formatCurrency(piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].jaehrlich)
          }}
        </option>
        <option
          v-bind:value="2"
          v-if="piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].halbjaehrlich >= 25"
        >
          Halbjährlich -
          {{
            formatCurrency(
              piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].halbjaehrlich,
            )
          }}
        </option>
        <option
          v-bind:value="4"
          v-if="
            piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].vierteljaehrlich >= 25
          "
        >
          Vierteljährlich -
          {{
            formatCurrency(
              piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].vierteljaehrlich,
            )
          }}
        </option>
        <option
          v-bind:value="12"
          v-if="piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].monatlich >= 25"
        >
          Monatlich -
          {{
            formatCurrency(piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].monatlich)
          }}
        </option>
      </select>
      <validierungs-fehler :fehler-text="fehler.tariffPaymentFrequency" :reduzierter-abstand="true" />
      <info-box id="tariffPaymentFrequencyInfo">
        Die Teilzahlungszuschläge betragen bei:<br />
        - halbjährlicher Zahlung 3% p.a. nominal (12,75% effektiver Jahreszins),<br />
        - vierteljährlicher Zahlung 5% p.a. nominal (14,10% effektiver Jahreszins),<br />
        - monatlicher Zahlung 5% p.a. nominal (11,35% effektiver Jahreszins).
      </info-box>
    </zelle>
  </zeile>

  <zeile v-if="!istEinmalbeitrag && istFerienhaus">
    <titel-zelle :zeige-info="true" info-box-id="tariffPaymentFrequencyInfo">Zahlungsweise</titel-zelle>
    <zelle>
      <select
        id="tariffPaymentFrequency"
        name="tariffPaymentFrequency"
        v-model.number="piniaStore.state.tarifinformationen.zahlungsweise"
        class="c-form-control-select"
        data-cy="zahlungsweise"
        required
        aria-required="true"
        data-fehlermeldung="Bitte wählen Sie eine Zahlungsweise aus."
        aria-label="Zahlungsweise"
        aria-describedby="tariffPaymentFrequencyInfo"
      >
        <option v-bind:value="1">
          Jährlich -
          {{ formatCurrency(gesamtPreisJaehrlichFH) }}
        </option>
        <option v-bind:value="2" v-if="pruefeHalbjaehrlichFH">
          Halbjährlich -
          {{ formatCurrency(gesamtPreisHalbjaehrlichFH) }}
        </option>
        <option v-bind:value="4" v-if="pruefeVierteljaehrlichFH">
          Vierteljährlich -
          {{ formatCurrency(gesamtPreisVieteljaehrlichFH) }}
        </option>
        <option v-bind:value="12" v-if="pruefeMonatlichFH">
          Monatlich -
          {{ formatCurrency(gesamtPreisMonatlichFH) }}
        </option>
      </select>
      <validierungs-fehler :fehler-text="fehler.tariffPaymentFrequency" :reduzierter-abstand="true" />
      <info-box id="tariffPaymentFrequencyInfo">
        Die Teilzahlungszuschläge betragen bei:<br />
        - halbjährlicher Zahlung 3% p.a. nominal (12,75% effektiver Jahreszins),<br />
        - vierteljährlicher Zahlung 5% p.a. nominal (14,10% effektiver Jahreszins),<br />
        - monatlicher Zahlung 5% p.a. nominal (11,35% effektiver Jahreszins).
      </info-box>
    </zelle>
  </zeile>

  <zeile :spacing-y="false">
    <titel-zelle>Zahlungsart</titel-zelle>
    <zelle class="mkRadio">
      <input
        v-model="piniaStore.state.zahlungsdaten.zahlungsart"
        type="radio"
        id="paymentMethodDebit"
        name="paymentMethod"
        value="debit"
        data-cy="zahlungsartLastschrift"
        required
        aria-required="true"
        aria-label="Lastschrift"
        data-fehlermeldung="Bitte wählen Sie eine Zahlungsart aus."
      />
      <label for="paymentMethodDebit" class="radiolabel">Lastschrift</label>
      <input
        v-model="piniaStore.state.zahlungsdaten.zahlungsart"
        type="radio"
        id="paymentMethodInvoice"
        name="paymentMethod"
        value="invoice"
        data-cy="zahlungsartRechnung"
        :disabled="!rechnungszahlungAktiv"
        aria-label="Rechnung"
      />
      <label
        for="paymentMethodInvoice"
        class="radiolabel"
        :style="{
          color: !rechnungszahlungAktiv ? '#898989' : 'unset',
        }"
      >
        Rechnung
        <span v-if="!istEinmalbeitrag"> &nbsp;(nur bei jährlicher Zahlungsweise) </span>
      </label>
      <validierungs-fehler :fehler-text="fehler.paymentMethod" :reduzierter-abstand="true" />
    </zelle>
  </zeile>

  <span v-if="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'">
    <zeile :spacing-y="false">
      <titel-zelle>Der Kontoinhaber ist</titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="paymentAccountHolderCustomer"
          name="paymentAccountHolder"
          v-model="piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle"
          :value="'versicherungsnehmer'"
          :required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
          :aria-required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
          aria-label="Der Kontoinhaber ist der Versicherungsnehmer"
          data-fehlermeldung="Sind Sie selbst oder eine andere Person der Kontoinhaber?"
          data-cy="kontoinhaberVersicherungsnehmer"
        />
        <label for="paymentAccountHolderCustomer" class="radiolabel">Der Versicherungsnehmer</label>
        <span v-if="piniaStore.state.personendaten.verwalterAktiv">
          <input
            type="radio"
            id="paymentAccountHolderVerwalter"
            name="paymentAccountHolder"
            v-model="piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle"
            :value="'verwalter'"
            :required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
            :aria-required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
            aria-label="Der Kontoinhaber ist der Verwalter"
            data-fehlermeldung="Sind Sie selbst oder eine andere Person der Kontoinhaber?"
            data-cy="kontoinhaberVerwalter"
          />
          <label for="paymentAccountHolderVerwalter" class="radiolabel">Der Verwalter</label>
        </span>
        <input
          type="radio"
          id="paymentAccountHolderOther"
          name="paymentAccountHolder"
          v-model="piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle"
          :value="'anderePerson'"
          :required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
          :aria-required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
          aria-label="Der Kontoinhaber ist eine andere Person"
          data-fehlermeldung="Sind Sie selbst oder eine andere Person der Kontoinhaber?"
          data-cy="kontoinhaberAnderePerson"
        />
        <label for="paymentAccountHolderOther" class="radiolabel">andere Person</label>
        <validierungs-fehler :fehler-text="fehler.paymentAccountHolder" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <!-- Kontoinhaber andere Person: Vorname, Name -->
    <span v-if="kontoinhaberIstAnderePerson">
      <zeile>
        <titel-zelle>Vorname und Name</titel-zelle>
        <zelle :breite="3">
          <input
            type="text"
            id="paymentAccountHolderFirstName"
            name="paymentAccountHolderFirstName"
            v-model.lazy.trim="piniaStore.state.zahlungsdaten.kontoinhaberPartner.vorname"
            class="c-form-control__input"
            placeholder="Vorname"
            minlength="2"
            maxlength="40"
            :required="kontoinhaberIstAnderePerson"
            :aria-required="kontoinhaberIstAnderePerson"
            aria-label="Vorname des Kontoinhabers"
            data-fehlermeldung="Bitte geben Sie den Vornamen ein (2-40 Zeichen)."
            data-cy="kontoinhaberPartnerVorname"
          />
          <validierungs-fehler :fehler-text="fehler.paymentAccountHolderFirstName" />
        </zelle>
        <zelle :breite="3">
          <input
            type="text"
            id="paymentAccountHolderName"
            name="paymentAccountHolderName"
            v-model.lazy.trim="piniaStore.state.zahlungsdaten.kontoinhaberPartner.nachname"
            class="c-form-control__input"
            placeholder="Name"
            minlength="2"
            maxlength="40"
            :required="kontoinhaberIstAnderePerson"
            :aria-required="kontoinhaberIstAnderePerson"
            aria-label="Nachname des Kontoinhabers"
            data-fehlermeldung="Bitte geben Sie den Nachnamen ein (2-40 Zeichen)."
            data-cy="kontoinhaberPartnerNachname"
          />
          <validierungs-fehler :fehler-text="fehler.paymentAccountHolderName" />
        </zelle>
      </zeile>
    </span>

    <zeile v-if="piniaStore.state.zahlungsdaten.kontoinhaberPartnerRolle !== ''">
      <titel-zelle>IBAN</titel-zelle>
      <zelle>
        <input
          type="text"
          id="paymentIban"
          name="paymentIban"
          v-model.trim="piniaStore.state.zahlungsdaten.iban"
          class="c-form-control__input"
          placeholder="IBAN"
          minlength="27"
          maxlength="27"
          :required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
          :aria-required="piniaStore.state.zahlungsdaten.zahlungsart === 'debit'"
          aria-label="IBAN"
          data-fehlermeldung="Bitte geben Sie eine deutsche IBAN ein (22 Zeichen)."
          data-cy="iban"
        />
        <validierungs-fehler :fehler-text="fehler.paymentIban" />
        <div class="notice-box__iban" id="paymentIbanInfo">
          <p>
            Ich ermächtige die GEV, Zahlungen von dem hier angegebenen Konto mittels Lastschrift einzuziehen. Zugleich
            wird das Kreditinstitut angewiesen, die von der GEV gezogenen Lastschriften einzulösen.<br />
            Hinweis: Innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, kann die Erstattung des belasteten
            Betrages verlangt werden. Es gelten dabei die mit dem Kreditinstitut vereinbarten Bedingungen.
          </p>
        </div>
      </zelle>
    </zeile>
  </span>
</template>
